/**
 * Instant-to-wall-time rendering for a caller-supplied IANA time zone —
 * built for exports where the reader's own time zone must never leak into
 * the numbers, only the zone the row itself carries (a billing export keyed
 * to a site's local hours, read on whatever laptop opens the file).
 *
 * Every value is read off the original instant through a single Intl call;
 * no intermediate zone-shifted Date is ever constructed, so there is no
 * local-getter read for a host time zone to corrupt. The rendered pattern
 * follows the caller's own date/time preference — display layout is a
 * viewer concern — while the instant and its UTC offset follow only the
 * supplied zone.
 *
 * Contains:
 * - formatInZoneWithOffset — instant + zone + pattern → wall time with a
 *   trailing explicit numeric UTC offset
 * - formatInZone — the same wall time without the offset, for on-screen
 *   display where the app chrome (not the cell text) carries which zone
 *   is shown
 *
 * Returns plain JS — no JSX.
 */

const PATTERN_TOKENS = /MMM|yyyy|MM|dd|HH|hh|mm|ss|a/g;

const FALLBACK_ZONE = 'UTC';

/**
 * Whether `Intl.DateTimeFormat` accepts the given zone identifier.
 */
function resolvableZone(timeZone) {
  if (!timeZone) return false;
  try {
    // eslint-disable-next-line no-new -- probe only; construction itself throws on an unknown zone
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Read every part this module needs off one instant, in one zone, through
 * one `Intl.DateTimeFormat` — the offset comes off the same authoritative
 * read as the wall-clock fields, so the two can never disagree the way a
 * zone-shifted `Date` and a separately-computed offset could.
 */
function zonedParts(date, timeZone) {
  const zone = resolvableZone(timeZone) ? timeZone : FALLBACK_ZONE;
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'longOffset',
  });
  const bag = {};
  for (const part of formatter.formatToParts(date)) {
    bag[part.type] = part.value;
  }
  const monthShort = new Intl.DateTimeFormat('en-US', { timeZone: zone, month: 'short' }).format(date);
  const hour24 = Number(bag.hour) % 24;
  const offset = (bag.timeZoneName || 'GMT+00:00').replace('GMT', '') || '+00:00';
  return {
    year: bag.year,
    month: bag.month,
    monthShort,
    day: bag.day,
    hour24,
    minute: bag.minute,
    second: bag.second,
    offset,
  };
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

/**
 * Substitute every pattern token in one pass so an emitted value (e.g. the
 * `Apr` from `MMM`) is never re-scanned as a token itself.
 */
function renderPattern(parts, pattern) {
  return pattern.replace(PATTERN_TOKENS, token => {
    switch (token) {
      case 'yyyy':
        return parts.year;
      case 'MMM':
        return parts.monthShort;
      case 'MM':
        return parts.month;
      case 'dd':
        return parts.day;
      case 'HH':
        return pad2(parts.hour24);
      case 'hh':
        return pad2(parts.hour24 % 12 === 0 ? 12 : parts.hour24 % 12);
      case 'mm':
        return parts.minute;
      case 'ss':
        return parts.second;
      case 'a':
        return parts.hour24 < 12 ? 'AM' : 'PM';
      default:
        return token;
    }
  });
}

/** Sentinel results `resolveZonedParts` can short-circuit both exports on. */
const EMPTY_VALUE = Symbol('empty');
const INVALID_VALUE = Symbol('invalid');

/**
 * Shared value-guard + zoned-parts resolution for both exports below —
 * either short-circuits before a zone is ever consulted.
 */
function resolveZonedParts(value, timeZone) {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return INVALID_VALUE;
  return zonedParts(date, timeZone);
}

/**
 * Render an instant in a specific time zone as wall-clock text with a
 * trailing explicit numeric UTC offset — e.g. `2026-03-08 01:59:59 -07:00`.
 *
 * @param {string|Date|null|undefined} value - The instant, as an ISO string
 *   or a `Date`. `null`/`undefined`/`''` render as `''`.
 * @param {string|null|undefined} timeZone - An IANA zone identifier. A
 *   missing or unresolvable zone falls back to UTC — never the caller's
 *   own (browser) zone.
 * @param {string} pattern - A date-fns-style pattern built only from this
 *   module's closed token set (`yyyy MMM MM dd HH hh mm ss a`), matching
 *   every combination `PersonalSection` offers plus its locale-derived
 *   fallback (`getDateTimeFormat`).
 * @returns {string} The rendered wall time plus offset, `''` for an empty
 *   value, or `'Invalid DateTime'` for an unparseable one.
 */
export function formatInZoneWithOffset(value, timeZone, pattern) {
  const parts = resolveZonedParts(value, timeZone);
  if (parts === EMPTY_VALUE) return '';
  if (parts === INVALID_VALUE) return 'Invalid DateTime';
  return `${renderPattern(parts, pattern)} ${parts.offset}`;
}

/**
 * Render an instant in a specific time zone as wall-clock text, with no
 * offset suffix — for on-screen cells where the surrounding UI (a label,
 * a tooltip) already states which zone is shown.
 *
 * @param {string|Date|null|undefined} value - Same contract as
 *   {@link formatInZoneWithOffset}.
 * @param {string|null|undefined} timeZone - Same contract as
 *   {@link formatInZoneWithOffset}.
 * @param {string} pattern - Same contract as {@link formatInZoneWithOffset}.
 * @returns {string} The rendered wall time, `''` for an empty value, or
 *   `'Invalid DateTime'` for an unparseable one.
 */
export function formatInZone(value, timeZone, pattern) {
  const parts = resolveZonedParts(value, timeZone);
  if (parts === EMPTY_VALUE) return '';
  if (parts === INVALID_VALUE) return 'Invalid DateTime';
  return renderPattern(parts, pattern);
}
